import { getEffectiveSelectionRange } from "../selection-tracker";
import {
  getNumber,
  getParagraphAt,
  getString,
  toBreakType,
  toInsertLocation,
} from "./utils";

type WriteTarget = "body" | "selection" | "paragraph";

interface ResolvedTarget {
  body?: Word.Body;
  kind: WriteTarget;
  paragraph?: Word.Paragraph;
  selection?: Word.Range;
}

async function resolveTarget(
  ctx: Word.RequestContext,
  args: Record<string, unknown>
): Promise<ResolvedTarget> {
  const target = getString(args, "target") as WriteTarget; // narrows string → union
  switch (target) {
    case "body":
      return { kind: "body", body: ctx.document.body };
    case "selection": {
      // Prefer the bookmark-backed cached selection so writes still
      // anchor correctly when focus is in the task pane.
      const effective = await getEffectiveSelectionRange(ctx);
      const selection = effective
        ? effective.range
        : ctx.document.getSelection();
      return { kind: "selection", selection };
    }
    case "paragraph": {
      const index = getNumber(args, "paragraphIndex");
      const paragraph = await getParagraphAt(ctx, index);
      return { kind: "paragraph", paragraph };
    }
    default:
      throw new Error(
        `Invalid target "${String(target)}". Expected one of: body, selection, paragraph.`
      );
  }
}

export async function insertTextCore(
  ctx: Word.RequestContext,
  args: Record<string, unknown> = {}
): Promise<{ ok: true }> {
  const text = getString(args, "text");
  const location = toInsertLocation(args.location);
  const t = await resolveTarget(ctx, args);
  // The Word API narrows InsertLocation per host method (e.g. body
  // disallows before/after). We accept any valid string at the boundary
  // and let Word raise InvalidOperation if the target/location combo is
  // illegal — keeps the executor uniform across targets.
  const loc = location as any;
  if (t.kind === "body" && t.body) {
    t.body.insertText(text, loc);
  } else if (t.kind === "selection" && t.selection) {
    t.selection.insertText(text, loc);
  } else if (t.kind === "paragraph" && t.paragraph) {
    t.paragraph.insertText(text, loc);
  }
  return { ok: true };
}

export async function insertParagraphCore(
  ctx: Word.RequestContext,
  args: Record<string, unknown> = {}
): Promise<{ ok: true }> {
  const text = getString(args, "text");
  const location = toInsertLocation(args.location);
  const t = await resolveTarget(ctx, args);
  const loc = location as any;
  if (t.kind === "body" && t.body) {
    t.body.insertParagraph(text, loc);
  } else if (t.kind === "selection" && t.selection) {
    t.selection.insertParagraph(text, loc);
  } else if (t.kind === "paragraph" && t.paragraph) {
    t.paragraph.insertParagraph(text, loc);
  }
  return { ok: true };
}

export async function insertBreakCore(
  ctx: Word.RequestContext,
  args: Record<string, unknown> = {}
): Promise<{ ok: true }> {
  const breakType = toBreakType(args.breakType);
  const location = toInsertLocation(args.location);
  const t = await resolveTarget(ctx, args);
  const loc = location as any;
  if (t.kind === "body" && t.body) {
    t.body.insertBreak(breakType, loc);
  } else if (t.kind === "selection" && t.selection) {
    t.selection.insertBreak(breakType, loc);
  } else if (t.kind === "paragraph" && t.paragraph) {
    t.paragraph.insertBreak(breakType, loc);
  }
  return { ok: true };
}

export async function insertHtmlCore(
  ctx: Word.RequestContext,
  args: Record<string, unknown> = {}
): Promise<{ ok: true }> {
  const html = getString(args, "html");
  const location = toInsertLocation(args.location);
  const t = await resolveTarget(ctx, args);
  const loc = location as any;
  if (t.kind === "body" && t.body) {
    t.body.insertHtml(html, loc);
  } else if (t.kind === "selection" && t.selection) {
    t.selection.insertHtml(html, loc);
  } else if (t.kind === "paragraph" && t.paragraph) {
    t.paragraph.insertHtml(html, loc);
  }
  return { ok: true };
}

export async function replaceSelectionCore(
  ctx: Word.RequestContext,
  args: Record<string, unknown> = {}
): Promise<
  | { ok: false; reason: "no_selection" }
  | { ok: true; replaced: true; source: "live" | "bookmark" }
> {
  const text = getString(args, "text");
  const effective = await getEffectiveSelectionRange(ctx);
  if (!effective) {
    return { ok: false, reason: "no_selection" };
  }
  effective.range.insertText(text, toInsertLocation("replace"));
  return { ok: true, replaced: true, source: effective.source };
}

export async function deleteSelectionCore(
  ctx: Word.RequestContext
): Promise<
  | { ok: false; reason: "no_selection" }
  | { ok: true; deleted: true; source: "live" | "bookmark" }
> {
  const effective = await getEffectiveSelectionRange(ctx);
  if (!effective) {
    return { ok: false, reason: "no_selection" };
  }
  effective.range.delete();
  return { ok: true, deleted: true, source: effective.source };
}

export async function clearSelectionCore(
  ctx: Word.RequestContext
): Promise<
  | { ok: false; reason: "no_selection" }
  | { ok: true; cleared: true; source: "live" | "bookmark" }
> {
  const effective = await getEffectiveSelectionRange(ctx);
  if (!effective) {
    return { ok: false, reason: "no_selection" };
  }
  effective.range.clear();
  return { ok: true, cleared: true, source: effective.source };
}
